import React from 'react';
import {
  Alert,
  Box,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import ProgressPanel from './ProgressPanel';

const RecommendPanel = ({
  t,
  loading,
  error,
  stage,
  progress,
  canRecommend,
  disabledReason,
  fastResult,
  bootstrapResult,
  recommendBootstrapSamples,
  onRecommendBootstrapSamplesChange,
  onRunFast,
  onRunBootstrap,
  onApplyRecommendation,
  formatNumber,
  formatPercent,
}) => (
  <Box sx={{ mt: 2 }}>
    <Typography variant="subtitle2">{t('estimationPage.recommendTitle')}</Typography>
    <Typography variant="caption" color="text.secondary">
      {t('estimationPage.recommendDescription')}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1, alignItems: 'center' }}>
      <Button
        variant="outlined"
        size="small"
        onClick={onRunFast}
        disabled={!canRecommend || loading}
      >
        {t('estimationPage.recommendFastButton')}
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={onRunBootstrap}
        disabled={!canRecommend || loading}
      >
        {t('estimationPage.recommendBootstrapButton')}
      </Button>
      <TextField
        size="small"
        type="number"
        label={t('estimationPage.recommendBootstrapSamplesLabel')}
        value={recommendBootstrapSamples}
        onChange={onRecommendBootstrapSamplesChange}
        inputProps={{ min: 10, max: 200 }}
        disabled={!canRecommend}
      />
    </Box>
    {disabledReason && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {disabledReason}
      </Typography>
    )}

    {loading && (
      <ProgressPanel
        size={24}
        title={t('estimationPage.recommendInProgress')}
        stage={stage}
        progress={progress}
        containerSx={{ py: 2 }}
      />
    )}

    {error && !loading && (
      <Alert severity="warning" sx={{ mt: 2 }}>
        {error}
      </Alert>
    )}

    {fastResult && !loading && (
      <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="subtitle2">{t('estimationPage.recommendFastLabel')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('estimationPage.recommendSummary', {
            minEvents: formatNumber(fastResult.minEvents),
            minActive: formatNumber(fastResult.minNonZeroYears),
            minSpan: formatNumber(fastResult.minSpanYears),
            stableRate: formatPercent(fastResult.stableRate),
            eligible: fastResult.eligibleCount,
            total: fastResult.totalGroups,
          })}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => onApplyRecommendation(fastResult)}
        >
          {t('estimationPage.recommendApply')}
        </Button>
      </Box>
    )}

    {bootstrapResult && !loading && (
      <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, border: '1px solid rgba(255,255,255,0.08)' }}>
        <Typography variant="subtitle2">{t('estimationPage.recommendBootstrapLabel')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('estimationPage.recommendSummary', {
            minEvents: formatNumber(bootstrapResult.minEvents),
            minActive: formatNumber(bootstrapResult.minNonZeroYears),
            minSpan: formatNumber(bootstrapResult.minSpanYears),
            stableRate: formatPercent(bootstrapResult.stableRate),
            eligible: bootstrapResult.eligibleCount,
            total: bootstrapResult.totalGroups,
          })}
        </Typography>
        <Button
          size="small"
          variant="outlined"
          sx={{ mt: 1 }}
          onClick={() => onApplyRecommendation(bootstrapResult)}
        >
          {t('estimationPage.recommendApply')}
        </Button>
      </Box>
    )}
  </Box>
);

export default RecommendPanel;
